import isEmpty from "lodash/isEmpty";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import * as styles from "web/react/components/feeds-filters/feed-filter-categories/feed-filter-categories.css";
import { GenderTabs, getGenderTabId } from "web/react/components/feeds-filters/gender-tabs";
import { useFilterContext } from "web/react/components/filters-emo/filter-container-emo";
import { CategoryItem } from "web/react/emo/category-item";
import { HStack } from "web/react/emo/h-stack";
import { Text } from "web/react/emo/text/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { ReduxStoreState } from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import { UniversalFilterOptionSerializer } from "web/types/serializers";
import {
    CategoryFilterOption,
    UniversalFilterOption,
    UniversalFilterSection,
} from "web/types/universal-filters";
import { CategoryBreadcrumbs } from "./feed-filter-categories-breadcrumbs";

const CATEGORY_PANEL_ID = "gender-tabpanel-categories";

export interface CategoryFilterProps {
    options: CategoryFilterOption[];
    id?: string;
    scrollOnLoad?: (id: string) => void;
    parent?: CategoryFilterOption;
    viewAllChecked?: boolean;
    genderFilterInfo: UniversalFilterSection;
}

export interface CategoryFilterChildrenProps extends CategoryFilterProps {
    onClick: (option: UniversalFilterOption | UniversalFilterOptionSerializer) => void;
    applyMultipleFilters: (
        options: (UniversalFilterOption | UniversalFilterOptionSerializer)[]
    ) => void;
}

export function CategoryItems({
    options,
    id,
    scrollOnLoad,
    parent,
    onClick,
    applyMultipleFilters,
    viewAllChecked = false,
    ...props
}: CategoryFilterChildrenProps): JSX.Element {
    const checkedOptions = options.filter(({ checked }) => !!checked) as CategoryFilterOption[];
    const { isMobileViewport, isTabletViewport } = useDomViewport();
    const viewSize = isMobileViewport ? "sm" : isTabletViewport ? "md" : "lg";

    return (
        <ol
            key={`category-list-${id}`}
            className={styles.catList[viewSize]}
            id={`category-list-${id}`}
        >
            <CategoryItem
                label={
                    parent?.label
                        ? gettext("feed.filters.category.view_all_category_name", {
                              // View All {category_name}
                              category_name: parent?.label,
                          })
                        : // View All Categories
                          gettext("feed.filters.category.view_all_categories")
                }
                onClick={() => {
                    if (checkedOptions.length) {
                        applyMultipleFilters(checkedOptions);
                    }
                }}
                showChevron={viewAllChecked}
                key={"view-all"}
                activeHighlighted={viewAllChecked}
                className={styles.catItem}
                scrollOnLoad={scrollOnLoad}
            />
            {options?.map((option) => {
                return (
                    <CategoryItem
                        label={option.label}
                        key={option.id}
                        id={option.id}
                        showChevron={option.checked}
                        activeHighlighted={option.checked}
                        {...{ option, ...props }}
                        onClick={() => {
                            onClick(option);
                        }}
                        className={styles.catItem}
                        scrollOnLoad={scrollOnLoad}
                        url={option.url}
                    />
                );
            })}
        </ol>
    );
}

export const recursiveMap = (
    options?: CategoryFilterOption[],
    flatCategories: any[] = [],
    parent?: CategoryFilterOption
): any[] => {
    const childCats = options?.find(({ checked }) => !!checked) as CategoryFilterOption;
    const viewAllChecked = !childCats; // view all will show active when no category is active in the list
    options && options.length > 0 && flatCategories.push({ options, parent, viewAllChecked });

    return childCats
        ? recursiveMap((childCats as any)?.children, flatCategories, childCats)
        : flatCategories;
};

const scrollOnLoadFn = (id: string): void => {
    const node = document.getElementById(id);

    node && node?.parentElement?.scroll({ top: node.offsetTop > 350 ? node.offsetTop : 0 });
    // 350px is added to avoid unnecessary scrolls
};

function extractSelectedGender(state: ReduxStoreState): string {
    return state.mainNavigationReducer.selectedGender;
}

export const Categories = ({
    options,
    scrollOnLoad = scrollOnLoadFn,
    ...props
}: CategoryFilterProps): JSX.Element => {
    const flatCategories = recursiveMap(options);

    const { isDesktopViewport } = useDomViewport();
    const { onFilterItemClick, applyMultiple, pendingFilterInfo, updateFromServer } =
        useFilterContext();
    const { genderFilterInfo, ...restProps } = props;

    const componentArray = (
        <CategoryItems
            id={`categories-${flatCategories.length - 1}`}
            scrollOnLoad={scrollOnLoad}
            onClick={onFilterItemClick}
            applyMultipleFilters={applyMultiple}
            {...flatCategories[flatCategories.length - 1]}
            {...restProps}
        />
    );

    const activeSelected = useSelector(extractSelectedGender);
    const isGenderEmpty = isEmpty(genderFilterInfo);
    const isGenderSelected =
        !isGenderEmpty &&
        genderFilterInfo.option_groups[0].options.some((option) => option.checked);

    function onGenderClick(option: UniversalFilterOption): void {
        if (option.checked) return;
        onFilterItemClick(option);
    }

    useEffect(() => {
        if (pendingFilterInfo.hasPending()) {
            updateFromServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingFilterInfo]);

    // If gender is already selected in the navbar default to that
    useEffect(() => {
        if (!isGenderEmpty && !isGenderSelected && activeSelected) {
            genderFilterInfo.option_groups[0].options.forEach((option) => {
                option.value.toLowerCase() === activeSelected &&
                    isDesktopViewport &&
                    onGenderClick(option);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedGenderOption = isGenderEmpty
        ? undefined
        : genderFilterInfo.option_groups[0].options.find((option) => !!option.checked);
    const panelProps = isGenderEmpty
        ? {}
        : {
              id: CATEGORY_PANEL_ID,
              role: "tabpanel",
              "aria-labelledby": selectedGenderOption
                  ? getGenderTabId(selectedGenderOption.id ?? String(selectedGenderOption.value))
                  : undefined,
          };

    return (
        <View marginTop={"none"}>
            <VStack spacing="xs" align="start">
                <div className={styles.wrapper}>
                    <div className={styles.scrollContainer}>
                        <VStack spacing={"sm"} justify="start">
                            {!isGenderEmpty && (
                                <GenderTabs
                                    options={genderFilterInfo.option_groups[0].options}
                                    onSelect={onGenderClick}
                                    panelId={CATEGORY_PANEL_ID}
                                />
                            )}

                            <div {...panelProps}>
                                <VStack spacing={"sm"} justify="start">
                                    <CategoryBreadcrumbs
                                        options={flatCategories}
                                        onClick={onFilterItemClick}
                                    />

                                    <HStack spacing="md" justify={"center"}>
                                        {isGenderSelected || isGenderEmpty ? (
                                            componentArray
                                        ) : (
                                            <div className={styles.noGenderLabel}>
                                                <Text textStyle={"body-s"} as={"p"}>
                                                    {/* "What gender are you shopping today?" */}
                                                    {gettext("feed.filters.no_gender")}
                                                </Text>
                                            </div>
                                        )}
                                    </HStack>
                                </VStack>
                            </div>
                        </VStack>
                    </div>
                </div>
            </VStack>
        </View>
    );
};
